// this thunk was previously part of the QUOTES reducer, but had to be moved to SHARED because it is also used by the USER reducer
// and was causing a circular dependency
import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { apiPostAuth } from '../../../amplify/restClient';
import { SET_MESSAGE, SET_APP_SIGNAL } from '../../actions/types';

dayjs.extend(utc);

const QUOTES_BOOTSTRAP_SESSION_KEY = 'quotesBootstrapDone';
const QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY = 'quotesIsolatedBootstrapDone';
const inMemoryBootstrapFlags = {};

function getSessionStorage() {
  if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
  if (typeof sessionStorage !== 'undefined') return sessionStorage;
  return null;
}

function hasBootstrapRun(sessionKey) {
  const storage = getSessionStorage();
  if (storage) return storage.getItem(sessionKey) === '1';
  return inMemoryBootstrapFlags[sessionKey] === '1';
}

function markBootstrapDone(sessionKey) {
  const storage = getSessionStorage();
  if (storage) storage.setItem(sessionKey, '1');
  inMemoryBootstrapFlags[sessionKey] = '1';
}

function clearBootstrapDone(sessionKey) {
  const storage = getSessionStorage();
  if (storage) storage.removeItem(sessionKey);
  delete inMemoryBootstrapFlags[sessionKey];
}

function toNumericDate(date) {
  if (typeof date === 'number' && Number.isFinite(date)) return date;
  if (typeof date === 'string' && /^\d+$/.test(date)) return Number(date);
  return null;
}

function normalizeDates(dates = []) {
  return [...new Set((dates || []).map((date) => toNumericDate(date)).filter((date) => date !== null))].sort((a, b) => a - b);
}

function maxDateOrNow(dates = []) {
  if (dates.length > 0) return Math.max(...dates);
  return dayjs.utc().startOf('month').startOf('day').valueOf();
}

function generateMonthStartDates(start, end) {
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return [];

  const output = [];
  let cursor = dayjs.utc(start).startOf('month').startOf('day').valueOf();
  const limit = dayjs.utc(end).startOf('month').startOf('day').valueOf();

  while (cursor <= limit) {
    output.push(cursor);
    cursor = dayjs.utc(cursor).add(1, 'month').startOf('month').startOf('day')
      .valueOf();
  }

  return output;
}

function collectDataTransactions(state) {
  const categories = Object.values(state?.data || {});
  return categories.flatMap((categoryData) => (categoryData?.transactions || []).concat(categoryData?.simulatedTransactions || []));
}

function getProviderAssetIdKey(providerAssetId) {
  if (!providerAssetId) return '';
  if (typeof providerAssetId === 'object') return JSON.stringify(providerAssetId);
  return String(providerAssetId);
}

function toApiAsset(asset) {
  return {
    ...asset,
    providerAssetId: asset?.providerAssetId || null,
    currency: asset?.currency || asset?.transactionCurrency || asset?.assetCurrency || asset?.accountCurrency,
  };
}

function assetScopeKey(asset) {
  const normalized = toApiAsset(asset || {});
  const currency = normalized?.currency || '';
  return `${normalized?.assetId || ''}|${getProviderAssetIdKey(normalized?.providerAssetId)}|${currency}`;
}

function dedupeAssets(assets = []) {
  const seen = new Set();
  const output = [];

  assets.forEach((asset) => {
    const normalized = toApiAsset(asset || {});
    if (!normalized?.assetId) return;
    const key = assetScopeKey(normalized);
    if (seen.has(key)) return;
    seen.add(key);
    output.push(normalized);
  });

  return output;
}

function getAssetsFromScopes(scopes = []) {
  return dedupeAssets((scopes || []).map((scope) => scope?.asset).filter(Boolean));
}

function normalizeScope(scope = {}) {
  const asset = toApiAsset(scope?.asset || {});
  if (!asset?.assetId) return null;

  const requiredDates = normalizeDates(scope?.requiredDates || []);
  const fallbackDate = requiredDates.length > 0 ? Math.max(...requiredDates) : dayjs.utc().startOf('month').startOf('day').valueOf();
  const rawFromDate = toNumericDate(scope?.fromDate);
  const rawToDate = toNumericDate(scope?.toDate);
  const fromDate = rawFromDate === null ? fallbackDate : rawFromDate;
  const toDate = rawToDate === null ? Math.max(fromDate, fallbackDate) : rawToDate;
  const normalizedFrom = Math.min(fromDate, toDate);
  const normalizedTo = Math.max(fromDate, toDate);
  const normalizedRequiredDates = requiredDates.length > 0 ? requiredDates : [normalizedTo];

  return {
    asset,
    fromDate: normalizedFrom,
    toDate: normalizedTo,
    requiredDates: normalizedRequiredDates,
    includeManualQuotes: scope?.includeManualQuotes ?? true,
  };
}

function normalizeScopes(scopes = []) {
  const byKey = {};

  (scopes || []).forEach((scope) => {
    const normalizedScope = normalizeScope(scope);
    if (!normalizedScope) return;
    const key = assetScopeKey(normalizedScope.asset);
    const existing = byKey[key];
    if (!existing) {
      byKey[key] = normalizedScope;
      return;
    }

    byKey[key] = {
      ...existing,
      fromDate: Math.min(existing.fromDate, normalizedScope.fromDate),
      toDate: Math.max(existing.toDate, normalizedScope.toDate),
      requiredDates: normalizeDates((existing.requiredDates || []).concat(normalizedScope.requiredDates || [])),
      includeManualQuotes: Boolean(existing.includeManualQuotes || normalizedScope.includeManualQuotes),
    };
  });

  return Object.keys(byKey).sort().map((key) => byKey[key]);
}

function getDatesFromScopes(scopes = []) {
  const dates = [];
  (scopes || []).forEach((scope) => {
    dates.push(...normalizeDates(scope?.requiredDates || []));
    const fromDate = toNumericDate(scope?.fromDate);
    if (fromDate !== null) dates.push(fromDate);
    const toDate = toNumericDate(scope?.toDate);
    if (toDate !== null) dates.push(toDate);
  });
  return normalizeDates(dates);
}

function getExpandedDatesFromScopes(scopes = []) {
  const dates = [];
  (scopes || []).forEach((scope) => {
    const fromDate = toNumericDate(scope?.fromDate);
    const toDate = toNumericDate(scope?.toDate);
    if (fromDate !== null) dates.push(fromDate);
    if (toDate !== null) dates.push(toDate);
    dates.push(...normalizeDates(scope?.requiredDates || []));
    if (fromDate !== null && toDate !== null) {
      dates.push(...generateMonthStartDates(Math.min(fromDate, toDate), Math.max(fromDate, toDate)));
    }
  });
  return normalizeDates(dates);
}

function collectScopeCandidatesFromTransaction(transaction = {}) {
  const category = transaction?.category;
  const providerAssetId = transaction?.providerAssetId || null;
  const currency = transaction?.assetCurrency || transaction?.accountCurrency || transaction?.transactionCurrency || transaction?.currency;
  const scopeCandidates = [];

  if (transaction?.assetId) {
    scopeCandidates.push({
      assetId: transaction.assetId,
      providerAssetId,
      currency,
      category,
    });
  }

  ['transactionCurrency', 'accountCurrency', 'assetCurrency', 'currency'].forEach((currencyField) => {
    const currencyCode = transaction?.[currencyField];
    if (!currencyCode) return;
    scopeCandidates.push({
      assetId: currencyCode,
      providerAssetId: null,
      currency: currencyCode,
      category,
    });
  });

  return dedupeAssets(scopeCandidates);
}

function extractAssetsFromTransactions(transactions = []) {
  return dedupeAssets(transactions.flatMap((transaction) => collectScopeCandidatesFromTransaction(transaction)));
}

function extractAssetDateBoundsFromTransactions(transactions = []) {
  const bounds = {};

  transactions.forEach((transaction) => {
    const numericDate = toNumericDate(transaction?.date);
    if (numericDate === null) return;

    collectScopeCandidatesFromTransaction(transaction).forEach((asset) => {
      const key = assetScopeKey(asset);
      if (!bounds[key]) {
        bounds[key] = { minDate: numericDate, maxDate: numericDate };
        return;
      }
      if (numericDate < bounds[key].minDate) bounds[key].minDate = numericDate;
      if (numericDate > bounds[key].maxDate) bounds[key].maxDate = numericDate;
    });
  });

  return bounds;
}

function normalizeIncomingRequestScope(body = {}) {
  const incomingScopes = Array.isArray(body?.scopes) ? body.scopes : [];
  const incomingAssets = incomingScopes.length > 0 ? getAssetsFromScopes(incomingScopes) : dedupeAssets(body?.assets || []);
  const incomingDates = incomingScopes.length > 0 ? getDatesFromScopes(incomingScopes) : normalizeDates(body?.dates || []);
  return { incomingAssets, incomingDates };
}

function buildAssetScopes({ assets, incomingDates, assetDateBounds }) {
  const upperBoundDate = maxDateOrNow(incomingDates);
  return (assets || []).map((asset) => {
    const normalizedAsset = toApiAsset(asset);
    const key = assetScopeKey(normalizedAsset);
    const bounds = assetDateBounds[key];
    const fromDate = bounds?.minDate ?? (incomingDates.length > 0 ? Math.min(...incomingDates) : upperBoundDate);
    const toDate = Math.max(bounds?.maxDate ?? fromDate, upperBoundDate);

    // Keep requiredDates explicit; backend expands monthly dates from fromDate/toDate.
    const requiredDates = normalizeDates(incomingDates);

    return {
      asset: normalizedAsset,
      fromDate,
      toDate,
      requiredDates: requiredDates.length > 0 ? requiredDates : [toDate],
      includeManualQuotes: true,
    };
  });
}

function buildScopedQuotesRequest(body, state, isBootstrapCall) {
  const explicitScopes = normalizeScopes(Array.isArray(body?.scopes) ? body.scopes : []);
  if (explicitScopes.length > 0) {
    const transactions = collectDataTransactions(state);
    const transactionAssets = extractAssetsFromTransactions(transactions);
    const assetDateBounds = extractAssetDateBoundsFromTransactions(transactions);
    const explicitScopeAssetKeys = new Set(explicitScopes.map((scope) => assetScopeKey(scope.asset)));
    const missingBootstrapAssets = isBootstrapCall
      ? transactionAssets.filter((asset) => !explicitScopeAssetKeys.has(assetScopeKey(asset)))
      : [];
    const explicitScopeDates = getDatesFromScopes(explicitScopes);
    const bootstrapScopes = buildAssetScopes({
      assets: missingBootstrapAssets,
      incomingDates: explicitScopeDates,
      assetDateBounds,
    });
    const scopes = normalizeScopes(explicitScopes.concat(bootstrapScopes));
    const isolatedDates = getExpandedDatesFromScopes(scopes);

    return {
      apiBody: {
        scopes,
        projectIds: Array.isArray(body?.projectIds) ? body.projectIds : [],
      },
      request: {
        scopes,
        assets: scopes.map((scope) => scope.asset),
        dates: isolatedDates,
        projectIds: Array.isArray(body?.projectIds) ? body.projectIds : [],
      },
      isolatedDates,
      cancelSignal: body?.cancelSignal,
    };
  }

  const { incomingAssets, incomingDates } = normalizeIncomingRequestScope(body);
  const transactions = collectDataTransactions(state);
  const transactionAssets = extractAssetsFromTransactions(transactions);
  const assetDateBounds = extractAssetDateBoundsFromTransactions(transactions);
  const requestAssets = isBootstrapCall ? dedupeAssets(incomingAssets.concat(transactionAssets)) : incomingAssets;
  const scopes = buildAssetScopes({ assets: requestAssets, incomingDates, assetDateBounds });

  // Isolated quotes endpoint still expects explicit dates; expand fromDate/toDate ranges for monthly isolated coverage.
  const isolatedDates = getExpandedDatesFromScopes(scopes);

  return {
    apiBody: {
      scopes,
      projectIds: Array.isArray(body?.projectIds) ? body.projectIds : [],
    },
    request: {
      scopes,
      assets: scopes.map((scope) => scope.asset),
      dates: isolatedDates,
      projectIds: Array.isArray(body?.projectIds) ? body.projectIds : [],
    },
    isolatedDates,
    cancelSignal: body?.cancelSignal,
  };
}

// accepts response from ECB API and returns an array with assetId: currCode, date, fx
// if expectedDate is set, the function will return quote items with that date even if it was not available from the API (it will copy the previous available date's quote)
export function transformEcbResponse(data, baseCurrency, expectedDate = null, expectedDateName = null) {
  const currencyCodes = data.structure.dimensions.series
    .find((s) => s.id === 'CURRENCY')
    .values.map((v) => v.id)
    .filter((v) => !['ARS', 'DZD', 'HRK', 'MAD', 'RUB', 'TWD'].includes(v)) // these codes are not available in the API, so we remove it from the list
    .concat('EUR');

  const timePeriods = data.structure.dimensions.observation // returns array of time periods as unix epoch
    .find((o) => o.id === 'TIME_PERIOD')
    .values.map((tp) => dayjs.utc(`${tp.start.split('+')[0]}Z`).valueOf());

  const { series } = data.dataSets[0];
  const observations = Object.values(series).map((x, idx) => x.observations); // returns an array of { "0": [1.2342], "1": [1.2433] ... } for each currencyCode, corresponding to time periods
  const fxValues = observations.map((o) => Object.values(o).flat()).concat([timePeriods.map(() => 1)]); // for EUR, add an array of 1s
  // returns an array of arrays [[1.2342, 1.2433], [5.2334, 5.1939], ... , [1, 1]

  const currentTpMissing = expectedDate && timePeriods[timePeriods.length - 1] !== expectedDate;
  // ↑↑ this is applicable to daily mode or to first day of the month before ECB has updated its data for the day
  // if there is no data for today, take the previous available TP (no matter how old)
  if (currentTpMissing) {
    timePeriods.push(expectedDateName); // add at the end
    fxValues.forEach((fx) => fx.push(fx[fx.length - 1])); // add at the end (copy from previous available TP)
  }

  const obj = [];
  const baseCurrencyIdx = currencyCodes.indexOf(baseCurrency);
  if (baseCurrencyIdx === -1 && baseCurrency !== 'EUR') throw new Error('transformEcbResponse: illegal baseCurrency');
  currencyCodes.forEach((currCode, idxCurrCode) => {
    timePeriods.forEach((tp, idxTp, self) => {
      obj.push({
        assetId: currCode,
        date: tp,
        quote: 1,
        // ↓↓ if baseCurrencyIdx not found, then it is EUR; otherwise divide everything by the baseCurrency rate (so that the baseCurrency rate itself is 1)
        quoteBaseCurrency: fxValues[idxCurrCode][idxTp] / (baseCurrencyIdx === -1 ? 1 : fxValues[baseCurrencyIdx][idxTp]),
        quoteDate: typeof tp !== 'number' ? self[idxTp - 1] : tp, // if it is a text and not a number, then it is the named baseline, so we take the previous date
        currency: currCode,
        source: 'api',
        projectId: null, // placeholder - project-related quotes
      }); // if baseCurrencyIdx not found, then it is EUR
    });
    return obj;
  }, {});

  return obj;
}

// arguments: dates[], projectIds[] (optional) for which the quotes are needed
// updates quotes slice only for the dates and assets retrieved from API

const getIsolatedQuotes = createAsyncThunk('quotes/getIsolatedQuotes', async (body, { dispatch }) => {
  const isBootstrapCall = !hasBootstrapRun(QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY);
  let requestProjectIds = [];
  if (!isBootstrapCall) requestProjectIds = Array.isArray(body?.projectIds) ? body.projectIds : [];
  const requestDates = normalizeDates(body?.dates || []);

  if (requestDates.length === 0) {
    dispatch({
      type: SET_APP_SIGNAL,
      payload: 'noProjectIdsSelected',
    });
    return {
      status: 'success',
      rows: [],
      request: { projectIds: requestProjectIds, dates: requestDates },
      meta: { isBootstrapCall, sessionKey: QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY },
    };
  }

  let bootstrapClaimed = false;
  try {
    if (isBootstrapCall) {
      // Claim isolated bootstrap before API starts so concurrent calls don't duplicate a full isolated refresh.
      markBootstrapDone(QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY);
      bootstrapClaimed = true;
    }

    const response = await apiPostAuth('myAPI', 'quotes/getIsolated', {
      body: { ...body, projectIds: requestProjectIds, dates: requestDates },
      headers: {
        'Content-Type': 'application/json',
      },
    }); // getIsolatedQuotes API expects { projectIds: [projectIds] optional, dates: [dates]}
    return {
      status: 'success',
      rows: response.quotes || [],
      request: { projectIds: requestProjectIds, dates: requestDates },
      meta: { isBootstrapCall, sessionKey: QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY },
    };
  } catch (error) {
    if (bootstrapClaimed) {
      // Isolated bootstrap failed, so allow the next isolated call to retry the full refresh.
      clearBootstrapDone(QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY);
    }

    dispatch({
      type: SET_MESSAGE,
      payload: 'dataUpdateError',
    });
    return {
      status: 'error',
      rows: [],
      request: { projectIds: requestProjectIds, dates: requestDates },
      meta: { isBootstrapCall, sessionKey: QUOTES_ISOLATED_BOOTSTRAP_SESSION_KEY },
    };
  }
});

// arguments: either
// - dates[], assets[{ assetId, providerAssetId, currency, [category] }]
// - scopes[{ asset, fromDate, toDate, requiredDates, includeManualQuotes }]
// for which the quotes are needed
// category is relevant mostly for crypto and stocks
// { aborted: true / false } is used by Prep component to cancel the API call when the component is re-rendered
// updates quotes slice only for the dates and assets retrieved from API

const getQuotes = createAsyncThunk('quotes/getQuotes', async (body, { getState, dispatch }) => {
  const todayUtcStart = dayjs.utc().startOf('day').valueOf();
  const initialState = getState();
  const settingsCurrent = toNumericDate(initialState?.user?.profile?.settings?.current);
  if (settingsCurrent !== todayUtcStart) {
    dispatch({
      type: 'user/setSettingsCurrent',
      payload: todayUtcStart,
    });
  }

  const state = settingsCurrent === todayUtcStart ? initialState : getState();
  const isBootstrapCall = !hasBootstrapRun(QUOTES_BOOTSTRAP_SESSION_KEY);
  const requestEnvelope = buildScopedQuotesRequest(body, state, isBootstrapCall);
  let bootstrapClaimed = false;

  if (!requestEnvelope.apiBody?.scopes?.length) {
    dispatch({
      type: SET_APP_SIGNAL,
      payload: 'noAssetsSelected',
    });
    return {
      status: 'success',
      rows: [],
      request: requestEnvelope.request,
      meta: { isBootstrapCall, sessionKey: QUOTES_BOOTSTRAP_SESSION_KEY },
    };
  }

  let arrayOfQuotes;
  try {
    if (isBootstrapCall) {
      // Claim bootstrap before API starts so parallel getQuotes calls in the same tick don't trigger duplicate full bootstrap requests.
      markBootstrapDone(QUOTES_BOOTSTRAP_SESSION_KEY);
      bootstrapClaimed = true;
    }

    const response = await apiPostAuth('myAPI', 'quotes/get', {
      body: requestEnvelope.apiBody,
      headers: {
        'Content-Type': 'application/json',
      },
    }); // getQuotes API expects { scopes: [{ asset, fromDate, toDate, requiredDates, includeManualQuotes }], projectIds: []}
    if (!requestEnvelope.cancelSignal?.aborted) {
      dispatch(getIsolatedQuotes({ projectIds: [], dates: requestEnvelope.isolatedDates }));
    }
    arrayOfQuotes = response.quotes;
    if (requestEnvelope.cancelSignal?.aborted) console.info('getQuotes aborted');
  } catch (error) {
    if (bootstrapClaimed) {
      // Bootstrap failed, so release the claim and allow the next getQuotes call to retry bootstrap.
      clearBootstrapDone(QUOTES_BOOTSTRAP_SESSION_KEY);
    }

    console.error('Error in getQuotes', error.response?.data);
    dispatch({
      type: SET_MESSAGE,
      payload: { message: 'dataUpdateError' },
    });
    return {
      status: 'error',
      rows: [],
      request: requestEnvelope.request,
      meta: { isBootstrapCall, sessionKey: QUOTES_BOOTSTRAP_SESSION_KEY },
    };
  }

  if (requestEnvelope.cancelSignal?.aborted) {
    if (bootstrapClaimed) {
      // Aborted bootstrap should not count as completed; keep session eligible for retry.
      clearBootstrapDone(QUOTES_BOOTSTRAP_SESSION_KEY);
    }
    return null;
  }

  // Keep the bootstrap claim set after success so subsequent calls stay scoped.

  return {
    status: 'success',
    rows: arrayOfQuotes,
    request: requestEnvelope.request,
    meta: { isBootstrapCall, sessionKey: QUOTES_BOOTSTRAP_SESSION_KEY },
  };
});

// arguments: dates[] for which the quotes are needed
// updates quotes.global.fx slice only for the dates retrieved from API (all currencies available in ECB)
// TODO get baseline & co from API as well

export default getQuotes;
export { getIsolatedQuotes };
